<script>
	import HyperparameterView from '../detail-view/Hyperparameterview.svelte';
  import Youtube from './Youtube.svelte';

	let softmaxEquation = `$$\\text{Softmax}(x_{i}) = \\frac{\\exp(x_i)}{\\sum_j \\exp(x_j)}$$`;
	let reluEquation = `$$\\text{ReLU}(x) = \\max(0,x)$$`;

  let currentPlayer;
</script>

<style>
	#description {
    margin-bottom: 60px;
    margin-left: auto;
    margin-right: auto;
    max-width: 78ch;
  }

  #description h2 {
    color: #444;
    font-size: 40px;
    font-weight: 450;
    margin-bottom: 12px;
    margin-top: 60px;
  }

  #description h4 {
    color: #444;
    font-size: 32px;
    font-weight: 450;
    margin-bottom: 8px;
    margin-top: 44px;
  }

  #description h6 {
    color: #444;
    font-size: 24px;
    font-weight: 450;
    margin-bottom: 8px;
    margin-top: 44px;
  }

  #description p {
    margin: 16px 0;
  }

  #description p img {
    vertical-align: middle;
  }

  #description .figure-caption {
    font-size: 13px;
    margin-top: 5px;
  }

  #description ol {
    margin-left: 40px;
  }

  #description p, 
  #description div,
  #description li {
    color: #555;
    font-size: 17px;
    line-height: 1.6;
  }

  #description small {
    font-size: 12px;
  }

  #description ol li img {
    vertical-align: middle;
  }

  #description .video-link {
    color: #3273DC;
    cursor: pointer;
    font-weight: normal;
    text-decoration: none;
  }

  #description ul {
      list-style-type: disc;
      margin-top: -10px;
      margin-left: 40px;
      margin-bottom: 15px;
  }
    
  #description a:hover, 
  #description .video-link:hover {
    text-decoration: underline;
  }

  .figure, .video {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
  }
</style>

<body>
  <div id="description">
    <h2>什么是卷积神经网络?</h2>
    <p>
      在机器学习中，分类器会给数据点分配一个类别标签。例如，<em  style="color:red;">图像分类器</em>会为图像中的目标对象输出一个类别标签（如鸟、飞机）。<em style="color:red;">卷积神经网络（CNN）</em>便是一种擅长解决上述问题的分类器！   
	 </p>
  	<p>
  		CNN是一种神经网络：一种用于在数据中识别模式的算法。神经网络通常由若干神经元组成，按层组织，每层都有自己的可学习权重和偏置。下面详细介绍CNN的基本组成部分。
  	</p>
  	<ol>
  		<li><strong style="color:blue;">张量</strong> 可以被视为一个n维矩阵。在上面的CNN中，张量将是3维的，输出层除外。</li>
  		<li><strong style="color:blue;">神经元</strong> 可以被视为一个接收多个输入并产生单个输出的函数。上图中神经元的输出以<span style="color:#FF7577;">红色</span> &rarr; <span style="color:#60A7D7;">蓝色</span> <strong>的激活映射表示。</strong>.</li>
  		<li><strong style="color:blue;">层</strong> 只是具有相同运算和相同超参数的一组神经元。</li>
  		<li><strong style="color:blue;">卷积核权重和偏置</strong>虽然对每个神经元都是独一无二的，但在训练阶段会被调整，使分类器能够适应所给的问题和数据集。它们在可视化中用<span style="color:#BC8435;">黄色</span> &rarr; <span style="color:#39988F;">绿色</span>的发散色标表示。具体数值可以在<em style="color:red;">交互式公式视图</em>点击某个神经元，或在<em style="color:red;">卷积弹性解释视图</em>中悬停卷积核/偏置来查看。</li>
  		<li><strong style="color:blue;">CNN</strong>输出一个<em style="color:red;">可微分的打分函数</em>，在输出层的可视化中表现为类别分数。</li>
  	</ol> 
  	<p>
  		如果你之前学过神经网络，这些术语听起来可能很熟悉。那么，CNN有何不同？CNN使用一种特殊类型的层，恰如其名为卷积层，使它们在从图像及类似图像的数据中学习时表现出色。关于图像数据，CNN可以用于多种计算机视觉任务，如<em style="color:red;">图像处理</em>、<em style="color:red;">分类</em>、<em style="color:red;">分割</em>和<em style="color:red;">目标检测</em>。
  	</p>  
  	<p>
      在CNN可视化中，你可以看到一个简单的CNN如何用于图像分类。由于网络结构的简单性，其性能并非完美无缺，但没关系！该示例中使用的网络结构<em style="color:red;">Tiny VGG</em>，包含了如今最先进的CNN所使用的许多相同层和操作，但规模更小。这样更易于上手理解。
      </p>     

      <h2>网络的每一层做什么？</h2>
      <p>
        让我们逐层了解网络。你可以通过点击和悬停在可视化的不同部分来互动。
      </p>
      <h4 id='article-input'>输入层</h4>
      <p>
        输入层（最左侧的层）表示进入CNN的输入图像。由于我们使用的是RGB图像，输入层有三个通道，分别对应红、绿、蓝通道，如该层所示。点击上方的<img class="is-rounded" width="12%" height="12%" src="PUBLIC_URL/assets/figures/network_details.png" alt="network details icon"/> 图标以显示该层及其他层的详细信息。
      </p>
      <h4 id='article-convolution'>卷积层s</h4>
      <p>
        卷积层是CNN的基础，因为它们包含学习到的卷积核（权重和偏置），用于提取能将不同图像区分开来的特征——这就是我们用于分类的目标！在你与卷积层互动时，你会看到前一层与卷积层之间的连接。每条连接代表一个唯一的卷积核，它用于卷积运算以产生当前卷积神经元的输出或激活图。  
  	</p>
  	<p>
      卷积神经元用一组卷积核与前一层相应神经元的输出执行逐元素点积运算。这会产生与卷积核数量相同的中间结果。所有中间结果相加再加上学习得到的偏置，就得到该卷积神经元的输出。
  	</p>
  	<p>
      例如，看看下方Tiny VGG架构中的第一层卷积层。请注意这一层有10组卷积神经元，而前一层只有3组特征神经元。观察第一层中最顶层卷积神经元的输出，当你将鼠标悬停在激活映射上时，你会看到有3个卷积核。
  	</p>
    <div class="figure">
      <img src="PUBLIC_URL/assets/figures/convlayer_overview_demo.gif" alt="clicking on topmost first conv. layer activation map" width=60% height=60% align="middle"/>
      <div class="figure-caption">
        图1 当你将鼠标悬停在第一层卷积层中最顶层节点的激活图上时，你可以看到应用了3个卷积核来生成该激活图。点击该激活图后，你可以看到对每个卷积核执行的卷积运算。
  	  </div>
    </div>

  	<p>
  		这些卷积核的大小是网络结构设计者指定的一个超参数。为了得到卷积神经元的输出（激活图），我们必须对前一层的输出与网络学习得到的卷积核执行逐元素点积。在TinyVGG中，点积操作的步长为1，这意味着卷积核每次在点积中向右或下一个像素处滑动，但这是一个超参数，网络结构设计者可以根据数据集进行调整。对所有3个卷积核都需要执行相同的操作，从而得到3个中间结果。
  	</p>
    <div class="figure">
      <img src="PUBLIC_URL/assets/figures/convlayer_detailedview_demo.gif" alt="clicking on topmost first conv. layer activation map" />
      <div class="figure-caption">
        图2 用于产生最顶层特征图中间结果的卷积核
      </div>
    </div>
  	<p>
      接着，对这3个中间结果以及网络所学习的偏置进行逐元素求和。之后，所得的二维张量将成为第一层卷积层中最顶层神经元的激活图，可在上方的界面查看。相同的操作也需要应用于生成每个神经元的激活图。
  	</p>
  	<p>
      通过一些简单的数学，我们可以推断第一卷积层共使用3×10=30个卷积核，每个大小为3×3。卷积层与前一层之间的连通性是在构建网络架构时的设计决策，这会影响每个卷积层的卷积核数量。请在可视化中点击以更好地理解卷积层背后的运算。看看你是否能跟随上面的示例！
    </p>
    <h6>理解超参数</h6>
    <p>
    	<HyperparameterView/>
    </p>
    <ol>
    	<li><strong style="color:blue;">填充</strong>：当卷积核超出激活图边界时，通常需要填充（Padding）。在激活图边缘填充可保护数据，从而提升性能，并有助于保持输入的空间尺寸，使架构设计者能够构建更深、性能更高的网络。存在多种填充技术，但最常用的方法是零填充，因为它的性能、简单性和计算效率较高。该方法是在输入边缘对称地添加零值，许多高性能的CNN（如 AlexNet）也采用了这种方法。</li>
    	<li><strong style="color:blue;">卷积核大小</strong>：通常也称为过滤器大小，指的是在输入上滑动窗口的维度。选择这个超参数对图像分类任务有深远影响。例如，较小的卷积核可以从输入中提取更多包含局部特征的信息。如上图的可视化所示，较小的卷积核也会导致特征图维度的下降幅度较小，从而允许更深的架构。相反，较大的卷积核提取的信息更少，导致特征图维度下降得更快，往往导致性能下降。较大的卷积核更适合提取较大尺度的特征。总之，选择合适的卷积核大小取决于任务和数据集，但通常较小的卷积核在图像分类任务中表现更好，因为架构设计者能够堆叠更多层来学习更复杂的特征！</li>
    	<li><strong style="color:blue;">步长</strong>：表示卷积核在一次滑动中应移动多少像素。例如，如上文的卷积层示例所述，Tiny VGG的卷积层步长为1，这意味着在输入的3×3窗口上执行点积以产生一个输出值，然后在随后的操作中向右再移动一个像素。步长对CNN的影响与卷积核大小相似。步长越小，学习到的特征越多，因为提取的数据越多，这也会导致输出层尺寸增大。相反，步长越大，特征提取越受限，输出层尺寸也越小。架构设计者的一个职责是确保在实现CNN时，卷积核能够对输入进行对称滑动。使用下方的超参数可视化，在不同输入/卷积核大小上调整步幅，以理解这一影响!</li>
    </ol>
    <h4>激活函数</h4>
    <h6 id='article-relu'>ReLU</h6>
    <p>
      神经网络在现代技术中极为普遍——因为它们如此准确！如今性能最强的CNN通常包含大量层，能够学习越来越多的特征。它们能够达到如此高的准确率，部分原因在于其非线性。ReLU为模型引入了所需的非线性。非线性对于产生非线性决策边界是必要的，这样输出就不能被写成输入的线性组合。如果没有非线性激活函数，深层CNN架构将退化为单层等效卷积层，性能将大打折扣。ReLU激活函数作为一种非线性激活函数使用，与Sigmoid等其他非线性函数相比，使用ReLU的CNN训练速度更快。
    </p>
    <p>
      ReLU激活函数是逐元素的数学运算：{reluEquation}
    </p>
    <div class="figure">
    <img src="PUBLIC_URL/assets/figures/relu_graph.png" alt="relu graph" width="30%" height="30%"/>
      <div class="figure-caption">
        图3：绘制的ReLU激活函数，忽略所有负数据
      </div>
    </div>
    <p>
      该激活函数对输入张量的每一个数值逐元素应用。例如，对值2.24应用ReLU，结果为2.24，因为2.24大于 0。你可以通过在上方网络中点击ReLU神经元来观察该激活函数的应用。ReLU（修正线性单元）是在上述网络架构中每个卷积层之后执行的。请注意这一层对网络中各个神经元激活图的影响！
    </p>
    <h6 id='article-softmax'>Softmax</h6>
    <p>
    	{softmaxEquation}
      Softmax的一个关键作用是确保CNN的输出和为1。因此，Softmax将输出缩放为概率。点击最后一层即可看到网络中的Softmax操作。注意展平后的logits并未缩放至0到1之间。为了直观显示每个logit的影响（未缩放的标量值），它们以<span style="color:#FFC385;">浅橙色</span> &rarr; <span style="color:#C44103;">深橙色</span>的颜色编码。经过Softmax函数后，每个类别现在对应一个合适的概率！
    </p>
    <p>
      你可能在想标准归一化和Softmax的区别——毕竟，两者都将logits缩放至0到1之间。记住，反向传播是训练神经网络的关键，我们希望正确答案具有最大的“信号”。通过使用Softmax，我们实质上是在获得可微性的同时近似argmax。简单来说，Softmax是一个“更柔和的”argmax？
    </p>
    <div class="figure">
    <img src="PUBLIC_URL/assets/figures/softmax_animation.gif" alt="softmax interactive formula view"/>
      <div class="figure-caption">
        图4：<strong style="color:blue;">Softmax交互公式视图</strong>允许用户与颜色编码的logits和公式进行交互，以理解展平层之后的预测分数如何归一化以产生分类分数。
      </div>
    </div>
    <h4 id='article-pooling'>池化层</h4>
    <p>
      不同的CNN架构中存在多种池化层，但它们的共同目标是逐步降低网络的空间尺度，从而减少参数量和整体计算量。上述Tiny VGG架构中使用的池化类型是最大池化（Max-Pooling）。
    </p>
    <p>
    	Max-Pooling操作在架构设计阶段同样需要选择卷积核大小和步长。一旦选定，操作就会以指定的步长在输入上滑动卷积核，在每个卷积核切片处仅选取输入中的最大值来得到输出值。可以通过点击网络中的池化神经元来观察这一过程。
    </p>
    <p>
      在上述Tiny VGG架构中，池化层使用2×2的卷积核、步长为2。按照这些超参数设定，最大池化操作会丢弃75%的激活值。通过丢弃如此多的数值，Tiny VGG的计算效率提高且更易避免过拟合。
    </p>
    <h4 id='article-flatten'>展平层</h4>
    <p>      
      该层将网络中的三维层转换为一维向量，便于输入到用于分类的全连接层。例如，一个5×5×2的张量将被转换为大小为50的向量。网络前面的卷积层已经从输入图像中提取了特征，但现在是对这些特征进行分类的时候。我们使用Softmax函数对这些特征进行分类，这需要一维输入。这就是展平层存在的原因。可以通过点击任一输出类别来查看此层。
    </p>

    <h2>交互特性</h2>
    <ol>
    	<li><strong style="color:blue;">上传自己的图片</strong>通过<em style="color:red">选择上传图片</em>图标<img class="icon is-rounded" src="PUBLIC_URL/assets/figures/upload_image_icon.png" alt="upload image icon"/> 来上传图片，了解你的图片如何被分类到10个类别中。通过分析网络中的神经元，你可以理解激活图及提取的特征。</li>
    	<li><strong style="color:blue;">改变激活图颜色尺度</strong>通过调整<em style="color:red">热力图的颜色尺度</em><img class="is-rounded" width="12%" height="12%" src="PUBLIC_URL/assets/figures/heatmap_scale.png" alt="heatmap"/>，了解在不同抽象层次上激活的影响。</li>
    	<li><strong style="color:blue;">理解网络细节</strong>通过点击<em style="color:red">网络细节图标</em><img class="is-rounded" width="12%" height="12%" src="PUBLIC_URL/assets/figures/network_details.png" alt="network details icon"/>，了解网络的层维度和颜色尺度等细节。</li>
    	<li><strong style="color:blue;">模拟网络运算</strong>通过点击<em style="color:red">播放图标</em><img class="icon is-rounded" src="PUBLIC_URL/assets/figures/play_button.png" alt="play icon"/> 来模拟网络运算，或在<em style="color:red">交互式公式视图</em>中悬停输入或输出的部分，理解映射关系与底层运算。</li>
      <li><strong style="color:blue;">学习层功能</strong>通过点击<em style="color:red">交互式公式视图图标</em><img class="icon is-rounded" src="PUBLIC_URL/assets/figures/info_button.png" alt="info icon"/>，学习层的功能。</li>
    </ol> 
  </div>
</body>
